import { DateTime } from 'luxon'

import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { useEventSlug } from '@/shared/hooks/useEventSlug'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { Card } from '@/shared/ui/Card'

import { useQuestsQuery } from '../model/queries'

import type { Quest } from '../model/types'

const formatWindow = (quest: Quest) => {
  const startAt = quest.start_at ? DateTime.fromISO(quest.start_at) : null
  const endAt = quest.end_at ? DateTime.fromISO(quest.end_at) : null

  if (!startAt && !endAt) return null

  const format = (date: DateTime) => date.setLocale('pt-BR').toFormat('dd/LL/yyyy HH:mm')

  if (startAt && endAt) return `${format(startAt)} — ${format(endAt)}`
  if (startAt) return `A partir de ${format(startAt)}`
  return `Até ${format(endAt as DateTime)}`
}

export function QuestHubPage() {
  const eventSlug = useEventSlug()
  const { t } = useTranslation()
  const { push } = useAppRouter()
  const { data: quests = [], isLoading, isError, error } = useQuestsQuery(eventSlug)

  if (isLoading) return null

  if (isError) {
    const status = (error as { response?: { status?: number } })?.response?.status
    const message =
      status === 401 ? t('quests.hub.unauthorizedError') : t('quests.hub.errorFallback')

    return <p role="alert">{message}</p>
  }

  if (quests.length === 0) {
    return <p>{t('quests.hub.empty')}</p>
  }

  return (
    <div className="flex flex-col gap-3">
      {quests.map(quest => (
        <Card
          key={quest.uid}
          className="cursor-pointer p-4"
          onClick={() => push(`/quests/${quest.uid}`)}
        >
          <p className="m-0 text-sm font-bold text-gray-900">{quest.name}</p>
          {formatWindow(quest) && (
            <p className="m-0 mt-1 text-xs text-gray-500">{formatWindow(quest)}</p>
          )}
        </Card>
      ))}
    </div>
  )
}
